"use client";

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  CaretLeft,
  CaretRight,
  Package,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  getCategories,
  addCategory,
  updateCategory,
  deleteCategory,
  reset,
} from "@/state/category/category.slice";

// ============================================
// LOADING SPINNER COMPONENT
// ============================================
const LoadingSpinner = ({ size = "default", text }) => (
  <div className="flex items-center justify-center gap-2">
    <Loader2
      className={`animate-spin ${size === "sm" ? "h-4 w-4" : size === "lg" ? "h-8 w-8" : "h-6 w-6"
        }`}
    />
    {text && <span className="text-sm text-muted-foreground">{text}</span>}
  </div>
);

// ============================================
// CATEGORY FORM COMPONENT (Reusable for Add/Edit)
// ============================================
const CategoryForm = ({
  category,
  onSubmit,
  onCancel,
  isLoading,
  mode = "add",
}) => {
  const [formData, setFormData] = useState({
    name: category?.name || "",
    slug: category?.slug || "",
    tax: category?.tax || 0,
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Auto-generate slug from name
  const handleNameChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      name: value,
      slug: value.toLowerCase().replace(/\s+/g, "-"),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      return;
    }
    onSubmit({
      ...formData,
      tax: parseFloat(formData.tax) || 0,
      ...(category?.id && { id: category.id }),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Category Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="e.g. Electronics, Fashion, Food"
          required
          disabled={isLoading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="slug">Slug</Label>
        <Input
          id="slug"
          value={formData.slug}
          onChange={(e) => handleChange("slug", e.target.value)}
          placeholder="e.g. electronics"
          disabled={isLoading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="tax">Tax Rate (%)</Label>
        <Input
          id="tax"
          type="number"
          step="0.01"
          min="0"
          max="100"
          value={formData.tax}
          onChange={(e) => handleChange("tax", e.target.value)}
          placeholder="0.00"
          disabled={isLoading}
        />
      </div>

      <DialogFooter className="pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading || !formData.name.trim()}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {mode === "add" ? "Adding..." : "Saving..."}
            </>
          ) : mode === "add" ? (
            "Add Category"
          ) : (
            "Save Changes"
          )}
        </Button>
      </DialogFooter>
    </form>
  );
};

// ============================================
// CATEGORY TABLE ROW COMPONENT
// ============================================
const CategoryTableRow = ({ category, onEdit, onDelete, isDeleting }) => {
  return (
    <TableRow className={isDeleting ? "opacity-50" : ""}>
      <TableCell className="font-medium">{category.name}</TableCell>
      <TableCell className="text-muted-foreground">{category.slug || "-"}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-blue-500" />
          <span className="font-semibold">{category.productCount || 0}</span>
        </div>
      </TableCell>
      <TableCell>{category.tax ? `${category.tax}%` : "-"}</TableCell>
      <TableCell>
        <Badge variant={category.status === 1 ? "success" : "secondary"}>
          {category.status === 1 ? "Active" : "Inactive"}
        </Badge>
      </TableCell>
      <TableCell className="text-right space-x-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(category)}
          disabled={isDeleting}
        >
          <PencilSimple className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(category)}
          disabled={isDeleting}
        >
          <Trash className="h-4 w-4 text-red-500" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

// ============================================
// PAGINATION COMPONENT
// ============================================
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Rows per page:</span>
        <Select
          value={itemsPerPage.toString()}
          onValueChange={(val) => onItemsPerPageChange(parseInt(val))}
        >
          <SelectTrigger className="w-[70px] h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="5">5</SelectItem>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="20">20</SelectItem>
            <SelectItem value="50">50</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-4">
        <span className="text-sm text-muted-foreground">
          {totalItems > 0
            ? `${startItem}-${endItem} of ${totalItems}`
            : "0 items"}
        </span>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            <CaretLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            <CaretRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN CATEGORIES PAGE COMPONENT
// ============================================
const CategoriesPage = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();

  // Redux state
  const { categories, isLoading, success, error, message } = useSelector(
    (state) => state.category
  );

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch categories on mount
  useEffect(() => {
    dispatch(getCategories());
  }, [dispatch]);

  // Handle success/error notifications
  useEffect(() => {
    if (success && message) {
      toast({
        title: "✓ Success",
        description: message,
        className: "bg-green-50 dark:bg-green-900/30 border-green-200",
      });
      dispatch(reset());
      setShowAddDialog(false);
      setShowEditDialog(false);
      setShowDeleteDialog(false);
      setIsSubmitting(false);
      setDeletingId(null);
      dispatch(getCategories());
    }
    if (error) {
      toast({
        title: "✕ Error",
        description: typeof error === "string" ? error : "An error occurred",
        variant: "destructive",
      });
      dispatch(reset());
      setIsSubmitting(false);
      setDeletingId(null);
    }
  }, [success, error, message, toast, dispatch]);

  // Filter categories based on search term
  const filteredCategories = useMemo(() => {
    if (!Array.isArray(categories)) return [];
    return categories.filter(
      (cat) =>
        cat.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cat.slug?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [categories, searchTerm]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Paginated data
  const paginatedCategories = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCategories.slice(start, start + itemsPerPage);
  }, [filteredCategories, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  // Summary stats
  const stats = useMemo(() => {
    const catList = Array.isArray(categories) ? categories : [];
    const totalProducts = catList.reduce(
      (sum, cat) => sum + (parseInt(cat.productCount) || 0),
      0
    );
    return {
      total: catList.length,
      active: catList.filter((c) => c.status === 1).length,
      totalProducts,
    };
  }, [categories]);

  // Handlers
  const handleAdd = useCallback(
    (data) => {
      setIsSubmitting(true);
      toast({
        title: "Creating category...",
        description: "Please wait while we add the category.",
      });
      dispatch(addCategory(data));
    },
    [dispatch, toast]
  );

  const handleEdit = useCallback((category) => {
    setSelectedCategory(category);
    setShowEditDialog(true);
  }, []);

  const handleUpdate = useCallback(
    (data) => {
      setIsSubmitting(true);
      toast({
        title: "Updating category...",
        description: "Please wait while we save changes.",
      });
      dispatch(updateCategory(data));
    },
    [dispatch, toast]
  );

  const handleDeleteClick = useCallback((category) => {
    setSelectedCategory(category);
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedCategory?.id) {
      setDeletingId(selectedCategory.id);
      setShowDeleteDialog(false);
      toast({
        title: "Deleting category...",
        description: `Removing "${selectedCategory.name}"...`,
      });
      dispatch(deleteCategory(selectedCategory.id));
    }
  }, [dispatch, selectedCategory, toast]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((count) => {
    setItemsPerPage(count);
    setCurrentPage(1);
  }, []);

  return (
    <div className="mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Categories</h1>
          <p className="text-muted-foreground">
            Manage product categories for your store
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} disabled={isLoading}>
          <Plus className="mr-2 h-4 w-4" /> Add Category
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Total Categories
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <div className="text-2xl font-bold">{stats.total}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Active Categories
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <div className="text-2xl font-bold text-green-600">
                {stats.active}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Products</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <div className="text-2xl font-bold text-blue-600">
                {stats.totalProducts}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400 dark:text-gray-500" />
          <Input
            className="pl-9"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {isLoading && (
          <div className="flex items-center">
            <LoadingSpinner size="sm" text="Loading..." />
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 shadow">
        {isLoading && !categories?.length ? (
          <div className="flex flex-col justify-center items-center h-48 gap-4">
            <LoadingSpinner size="lg" />
            <p className="text-muted-foreground">Loading categories...</p>
          </div>
        ) : paginatedCategories.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Products</TableHead>
                  <TableHead>Tax</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedCategories.map((category) => (
                  <CategoryTableRow
                    key={category.id}
                    category={category}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    isDeleting={deletingId === category.id}
                  />
                ))}
              </TableBody>
            </Table>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredCategories.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </>
        ) : (
          <div className="text-center py-12">
            <h3 className="text-lg font-semibold">No categories found</h3>
            <p className="text-gray-500 dark:text-gray-400 dark:text-gray-500">
              {searchTerm
                ? "Try a different search term"
                : "Add your first category to get started"}
            </p>
          </div>
        )}
      </div>

      {/* Add Category Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
            <DialogDescription>
              Create a new category to organize your products
            </DialogDescription>
          </DialogHeader>
          <CategoryForm
            onSubmit={handleAdd}
            onCancel={() => setShowAddDialog(false)}
            isLoading={isSubmitting}
            mode="add"
          />
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
            <DialogDescription>
              Update the details of this category
            </DialogDescription>
          </DialogHeader>
          <CategoryForm
            category={selectedCategory}
            onSubmit={handleUpdate}
            onCancel={() => setShowEditDialog(false)}
            isLoading={isSubmitting}
            mode="edit"
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedCategory?.name}"?
              {selectedCategory?.productCount > 0 && (
                <span className="block mt-2 text-orange-600">
                  Warning: This category has {selectedCategory.productCount}{" "}
                  products associated with it.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CategoriesPage;
